import { pluralize } from './misc.tsx'

/**
 * How a category or subcategory is named when more than one is meant.
 *
 * Every "Best ___ in DFW" heading, title tag, breadcrumb and nav label goes
 * through here.
 *
 * ## Why a database field rather than a rule
 *
 * `pluralize()` is suffix manipulation: add `s`, `y` to `ies`, `s` to `es`.
 * That is correct for agent nouns, which name the people or firms doing the
 * work: Plumber, Electrician, Roofing Contractor.
 *
 * It is wrong for service and mass nouns, which name the work itself and have
 * no distinct plural. Those produced "Best Pest Controls in Dallas-Fort Worth",
 * "Best Lawn Cares", "Best Bookkeepings", and — worst, because it changes the
 * meaning to artwork — "Best Paintings". Five of thirty categories and about
 * twenty of ninety-one subcategories were affected.
 *
 * No string rule can separate the two: English marks the distinction lexically,
 * not in the spelling. An exception list in code would work but would need a
 * deploy for every new category, and category-specific editorial belongs in
 * editable fields rather than in the bundle.
 *
 * ## Choosing a value
 *
 * For a mass noun the plural form is the singular: `Pest Control`. Search
 * volume in the DFW DMA supports leaving it bare rather than appending a
 * provider noun — "pest control dallas" draws 2,400/mo against 320 for "pest
 * control companies dallas". Append only where the bare noun genuinely loses,
 * as with Lawn Care, where "lawn care services dallas" (260) beats the bare
 * form (170).
 *
 * Note that Google Ads collapses singular and plural into one keyword, so
 * volume cannot say whether "Plumbers" or "Plumber" performs better. Agent
 * nouns stay pluralized because that reads correctly, and there is no SEO cost
 * either way.
 */
/**
 * `pluralName` is required rather than optional on purpose. Optional would let
 * a caller pass a row whose `select` omitted the column and silently fall back
 * to the broken rule — the failure would be a wrong heading in production, not
 * a type error. Required means every query that feeds a heading has to fetch
 * the field, and forgetting one fails the build.
 */
export function categoryPlural(entity: {
	name: string
	pluralName: string | null
}): string {
	return entity.pluralName?.trim() || pluralize(entity.name)
}
